import * as Sentry from '@sentry/react';

import {isChunkLoadError} from './errorReporting';

/**
 * Noise signatures dropped on every app's Sentry client. Applied via Sentry's
 * InboundFilters integration, so they catch the event regardless of capture
 * path — both Sentry's built-in global handlers and our own captureException.
 *
 * - "Object Not Found Matching Id" (#839/#840): an unhandledrejection injected
 *   by Outlook/Office SafeLink URL-scanning bots crawling links from emails. It
 *   originates in the crawler, not our app (plain-string rejection value, 0 real
 *   users impacted). The MethodName/ParamCount vary across hits, so this stable
 *   prefix is the fingerprint; no legitimate app error produces that string.
 * - "The database connection is closing" (#956): thrown inside Firebase Auth's
 *   IndexedDBLocalPersistence._withRetries when a background token refresh hits
 *   IndexedDB at the moment iOS Safari tears the connection down (tab
 *   backgrounded / frozen for BFCache / terminated). A transient DOMException
 *   (code 11) with no app frames that self-heals on the next foreground refresh.
 * - "Can't find variable: EmptyRanges" (JRFD-LOG-15): a Safari extension content
 *   script that wraps <video> playback. Its stack is `played@ / syncControl@ /
 *   handleEvent@` with no script URL at all (window.onerror reports
 *   filename "undefined"), and neither symbol exists anywhere in our source,
 *   our node_modules, or the deployed bundle — the injected script is the only
 *   place `EmptyRanges` is referenced, and it references it out of scope. We
 *   can't fix code we don't ship, and the page keeps working.
 */
export const SENTRY_IGNORE_ERRORS = [
    /Object Not Found Matching Id/,
    /The database connection is closing/,
    /Can't find variable: EmptyRanges/
];

/**
 * Initialise Sentry with the configuration shared across the Ember Tracking web
 * apps (embertracking + hub). No-op unless a dsn is provided and NODE_ENV is
 * production, so callers can invoke it unconditionally.
 *
 * @param {Object} options
 * @param {string} options.dsn - Sentry DSN (init is skipped when falsy)
 * @param {string} [options.release] - REACT_APP_SENTRY_RELEASE
 * @param {string} [options.environment='production'] - Sentry environment channel
 * @param {boolean} [options.enableLogs=false] - forward logs (e.g. via consoleLoggingIntegration) to Sentry
 * @param {Array} [options.ignoreErrors=[]] - extra patterns merged with SENTRY_IGNORE_ERRORS
 * @param {Array} [options.integrations=[]] - extra integrations merged with browserTracingIntegration
 * @param {(error:any, event:Object, hint:Object) => boolean} [options.shouldDropEvent]
 *        - app-specific beforeSend predicate; return true to drop the event. Runs
 *          after the shared chunk-load filter.
 */
export const initSentry = ({
    dsn,
    release,
    environment = 'production',
    enableLogs = false,
    ignoreErrors = [],
    integrations = [],
    shouldDropEvent
} = {}) => {
    if (!dsn || process.env.NODE_ENV !== 'production') {
        return;
    }

    Sentry.init({
        dsn,
        sendDefaultPii: false,
        enableLogs,
        environment,
        release,
        ignoreErrors: [...SENTRY_IGNORE_ERRORS, ...ignoreErrors],
        // Browser performance traces (web-vitals: LCP/FCP/INP/CLS/TTFB) at 50%
        // sampling so we can compare per-browser perf in Sentry (#698 — Safari
        // slowness reports). Raised from 0.1 to firm up the per-browser
        // percentiles (low absolute volume made the 10% sample noisy).
        //
        // Session Replay stays off (#698): rrweb recording + its compression
        // worker were the dominant main-thread/Safari cost on the dashboard, not
        // Firestore transport. See the issue for the QA profile.
        tracesSampleRate: 0.5,
        integrations: [Sentry.browserTracingIntegration(), ...integrations],
        beforeSend(event, hint) {
            const error = hint.originalException;
            const filename = event.exception?.values?.[0]?.stacktrace?.frames?.[0]?.filename || '';

            // Don't send chunk load errors — they're expected after deployments
            // when cached chunks are stale.
            if (isChunkLoadError(error, filename)) {
                return null;
            }

            if (shouldDropEvent?.(error, event, hint)) {
                return null;
            }

            return event;
        }
    });
};
